import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNotifications } from 'core/notifications/useNotifications';
import { useLookerPostMessage } from '../hooks/useLookerPostMessage';
import { OPACITY_HIDDEN, OPACITY_VISIBLE } from '../constants';
import { Spinner } from '@salecycle/ui-component-library';
import { useUrlGenerator } from '../data/useUrlGenerator';

const Frame = styled.iframe`
  border: none;
  height: 100%;
  width: 100%;
`;

interface Props {
  locale?: string;
}

export const LookerEmbed = ({ locale }: Props) => {
  const { t } = useTranslation('dashboard');
  const { showNotification } = useNotifications();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [frameDataLoaded, setFrameDataLoaded] = useState(false);
  const [frameHeight, setFrameHeight] = useState('100%');
  const { data, error: urlGeneratorError, isLoading } = useUrlGenerator(locale);

  const [lookerError] = useLookerPostMessage({
    urlLoaded: data?.url,
    frameRef: frameRef,
    setFrameHeight,
    setFrameDataLoaded,
  });

  useEffect(() => {
    if (!lookerError && !urlGeneratorError) {
      return;
    }

    showNotification(t('error.requestFailed'), 'error');
  }, [lookerError, urlGeneratorError, showNotification, t]);

  if (lookerError || urlGeneratorError) {
    return null;
  }

  const opacity = frameDataLoaded ? OPACITY_VISIBLE : OPACITY_HIDDEN;

  return (
    <>
      {(isLoading || !frameDataLoaded) && <Spinner />}
      {data?.url && (
        <div
          // We use a style attribute here due to a bug with styled-components where dynamic props will
          // cause fonts to be rerendered. This will cause fonts to jump around when we change the height of the iframe.
          // https://github.com/styled-components/styled-components/issues/1593
          style={{
            height: frameHeight,
            // We use `opacity` here because `visibility` and `display` prevent the embed
            // calculating its own height and emitting the event.
            opacity: opacity,
          }}
          data-testid="LookerEmbed"
        >
          <Frame ref={frameRef} title={t('heading')} src={data.url} height={frameHeight} />
        </div>
      )}
    </>
  );
};

export default LookerEmbed;
